import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { loadPdf, loadPdfBytes, renderPage } from '../utils/pdfRender.js';

/**
 * Renders every page of a PDF and lets a caller draw absolutely-positioned
 * children on top of each one.
 *
 * Shared by the staff field mapper (which overlays draggable boxes) and the
 * parent-facing filler (which overlays real inputs), so both agree exactly on
 * where a normalized rect lands on the page.
 *
 * `renderOverlay({ pageIndex, cssWidth, cssHeight, pdfWidth, pdfHeight })`
 * returns the overlay content for one page.
 */
function PdfPageStack({
  url,
  bytes,
  authToken,
  renderOverlay,
  onLoaded,
  onError,
  maxWidth = 900,
  className = '',
}) {
  const hostRef = useRef(null);
  const canvasRefs = useRef([]);
  const pdfRef = useRef(null);
  const [pageBoxes, setPageBoxes] = useState([]);
  const [numPages, setNumPages] = useState(0);
  const [status, setStatus] = useState('loading');
  const [errMsg, setErrMsg] = useState('');
  // Width is measured, not assumed, so the same map renders correctly on an iPad
  // kiosk, a laptop, and a phone.
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load the document once per source.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrMsg('');
    (async () => {
      try {
        const pdf = bytes ? await loadPdfBytes(bytes) : await loadPdf(url, { authToken });
        if (cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
        setStatus('ready');
        onLoaded?.({ numPages: pdf.numPages });
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        const msg =
          err?.name === 'MissingPDFException'
            ? 'The form file could not be found on the server.'
            : err?.name === 'UnexpectedResponseException'
              ? 'Not authorised to open this form.'
              : err?.message || 'This PDF could not be opened.';
        setErrMsg(msg);
        onError?.(err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, bytes, authToken]);

  // Draw pages whenever the document or the available width changes.
  // Runs are tokenised: a resize can start a second pass while the first is still
  // going, and without this the two would fight over the canvases and the stale
  // one would overwrite the fresh geometry.
  const runRef = useRef(0);
  const drawPages = useCallback(async () => {
    const pdf = pdfRef.current;
    if (!pdf || !containerWidth) return;
    const myRun = ++runRef.current;
    const width = Math.min(containerWidth, maxWidth);
    const boxes = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      if (runRef.current !== myRun) return; // superseded — let the newer pass win
      const canvas = canvasRefs.current[i - 1];
      if (!canvas) continue;
      try {
        boxes.push(await renderPage(pdf, i, canvas, width));
      } catch (err) {
        // A cancelled render is expected when a newer pass took over.
        if (err?.name === 'RenderingCancelledException') return;
        boxes.push(null); // a page that will not draw must not block the others
      }
    }
    if (runRef.current === myRun) setPageBoxes(boxes);
  }, [containerWidth, maxWidth]);

  useEffect(() => {
    if (status === 'ready') drawPages();
  }, [status, numPages, drawPages]);

  if (status === 'error') {
    return (
      <div className={`pdfstack pdfstack-error ${className}`} ref={hostRef}>
        <p className="pdfstack-error-msg">⚠️ {errMsg}</p>
      </div>
    );
  }

  return (
    <div className={`pdfstack ${className}`} ref={hostRef}>
      {status === 'loading' && <p className="pdfstack-loading">Loading form…</p>}
      {Array.from({ length: numPages }).map((_, i) => {
        const box = pageBoxes[i];
        return (
          <div className="pdfstack-page" key={i}>
            <div
              className="pdfstack-page-inner"
              style={box ? { width: box.cssWidth, height: box.cssHeight } : undefined}
            >
              <canvas
                ref={(el) => {
                  canvasRefs.current[i] = el;
                }}
                className="pdfstack-canvas"
              />
              {box && renderOverlay && (
                <div className="pdfstack-overlay">
                  {renderOverlay({
                    pageIndex: i,
                    cssWidth: box.cssWidth,
                    cssHeight: box.cssHeight,
                    pdfWidth: box.pdfWidth,
                    pdfHeight: box.pdfHeight,
                  })}
                </div>
              )}
            </div>
            {numPages > 1 && <div className="pdfstack-pagenum">Page {i + 1} of {numPages}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default PdfPageStack;
